import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { firstValueFrom, Observable } from "rxjs";
import {
  CriarTarefaPostModel,
  ModeloTarefaDTO,
  PainelAcompanhamentoItem,
  ResumoPreSessao,
  TarefaTerapeuticaDTO
} from "app/models/acompanhamento.model";

@Injectable({ providedIn: "root" })
export class AcompanhamentoService {
  constructor(private httpClient: HttpClient) { }

  obterPainel(): Promise<PainelAcompanhamentoItem[]> {
    return firstValueFrom(
      this.httpClient.get<PainelAcompanhamentoItem[]>(`${environment.urlApi}/Acompanhamento/painel`)
    );
  }

  obterResumoPreSessao(clienteId: number): Promise<ResumoPreSessao> {
    return firstValueFrom(
      this.httpClient.get<ResumoPreSessao>(`${environment.urlApi}/Acompanhamento/resumoPreSessao/${clienteId}`)
    );
  }

  listarTarefas(clienteId: number): Promise<TarefaTerapeuticaDTO[]> {
    return firstValueFrom(
      this.httpClient.get<TarefaTerapeuticaDTO[]>(`${environment.urlApi}/TarefaTerapeutica/cliente/${clienteId}`)
    );
  }

  criarTarefa(model: CriarTarefaPostModel): Promise<TarefaTerapeuticaDTO> {
    return firstValueFrom(
      this.httpClient.post<TarefaTerapeuticaDTO>(`${environment.urlApi}/TarefaTerapeutica/adicionar`, model)
    );
  }

  cancelarTarefa(tarefaId: number): Promise<TarefaTerapeuticaDTO> {
    return firstValueFrom(
      this.httpClient.post<TarefaTerapeuticaDTO>(`${environment.urlApi}/TarefaTerapeutica/cancelar/${tarefaId}`, {})
    );
  }

  /** Anexa (ou substitui) o áudio de orientação gravado pelo psicólogo. */
  anexarAudioTarefa(tarefaId: number, audio: Blob, duracaoSegundos: number): Promise<TarefaTerapeuticaDTO> {
    const form = new FormData();
    form.append('audio', audio, `orientacao-${tarefaId}.webm`);
    form.append('duracaoSegundos', String(Math.round(duracaoSegundos)));
    return firstValueFrom(
      this.httpClient.post<TarefaTerapeuticaDTO>(`${environment.urlApi}/TarefaTerapeutica/${tarefaId}/audio`, form)
    );
  }

  /** Baixa o áudio de orientação (autenticado) como Blob. */
  obterAudioTarefa(tarefaId: number): Observable<Blob> {
    return this.httpClient.get(`${environment.urlApi}/TarefaTerapeutica/${tarefaId}/audio`, { responseType: 'blob' });
  }

  /** Baixa o áudio de resposta do paciente (autenticado) como Blob. */
  obterRespostaAudioTarefa(tarefaId: number): Observable<Blob> {
    return this.httpClient.get(`${environment.urlApi}/TarefaTerapeutica/${tarefaId}/resposta-audio`, { responseType: 'blob' });
  }

  /** Anexa um arquivo (imagem/PDF) de apoio à tarefa. */
  anexarArquivoTarefa(tarefaId: number, arquivo: File): Promise<TarefaTerapeuticaDTO> {
    const form = new FormData();
    form.append('arquivo', arquivo, arquivo.name);
    return firstValueFrom(
      this.httpClient.post<TarefaTerapeuticaDTO>(`${environment.urlApi}/TarefaTerapeutica/${tarefaId}/anexo`, form)
    );
  }

  /** Anexa um link (página/vídeo) de apoio à tarefa. */
  anexarLinkTarefa(tarefaId: number, url: string, titulo?: string): Promise<TarefaTerapeuticaDTO> {
    return firstValueFrom(
      this.httpClient.post<TarefaTerapeuticaDTO>(`${environment.urlApi}/TarefaTerapeutica/${tarefaId}/anexo-link`, { url, titulo })
    );
  }

  /** Remove um anexo da tarefa. */
  removerAnexoTarefa(anexoId: number): Promise<TarefaTerapeuticaDTO> {
    return firstValueFrom(
      this.httpClient.delete<TarefaTerapeuticaDTO>(`${environment.urlApi}/TarefaTerapeutica/anexo/${anexoId}`)
    );
  }

  /** Baixa o arquivo de um anexo (autenticado) como Blob. */
  obterAnexoTarefa(anexoId: number): Observable<Blob> {
    return this.httpClient.get(`${environment.urlApi}/TarefaTerapeutica/anexo/${anexoId}`, { responseType: 'blob' });
  }

  // ---------- Modelos de tarefa ----------

  listarModelos(): Promise<ModeloTarefaDTO[]> {
    return firstValueFrom(this.httpClient.get<ModeloTarefaDTO[]>(`${environment.urlApi}/ModeloTarefa`));
  }

  criarModelo(titulo: string, descricao?: string): Promise<ModeloTarefaDTO> {
    return firstValueFrom(
      this.httpClient.post<ModeloTarefaDTO>(`${environment.urlApi}/ModeloTarefa`, { titulo, descricao })
    );
  }

  anexarAudioModelo(modeloId: number, audio: Blob, duracaoSegundos: number): Promise<ModeloTarefaDTO> {
    const form = new FormData();
    form.append('audio', audio, `modelo-${modeloId}.webm`);
    form.append('duracaoSegundos', String(Math.round(duracaoSegundos)));
    return firstValueFrom(
      this.httpClient.post<ModeloTarefaDTO>(`${environment.urlApi}/ModeloTarefa/${modeloId}/audio`, form)
    );
  }

  obterAudioModelo(modeloId: number): Observable<Blob> {
    return this.httpClient.get(`${environment.urlApi}/ModeloTarefa/${modeloId}/audio`, { responseType: 'blob' });
  }

  aplicarModelo(modeloId: number, clienteId: number): Promise<TarefaTerapeuticaDTO> {
    return firstValueFrom(
      this.httpClient.post<TarefaTerapeuticaDTO>(`${environment.urlApi}/ModeloTarefa/${modeloId}/aplicar`, { clienteId })
    );
  }

  excluirModelo(modeloId: number): Promise<void> {
    return firstValueFrom(this.httpClient.delete<void>(`${environment.urlApi}/ModeloTarefa/${modeloId}`));
  }

  anexarArquivoModelo(modeloId: number, arquivo: File): Promise<ModeloTarefaDTO> {
    const form = new FormData();
    form.append('arquivo', arquivo, arquivo.name);
    return firstValueFrom(
      this.httpClient.post<ModeloTarefaDTO>(`${environment.urlApi}/ModeloTarefa/${modeloId}/anexo`, form)
    );
  }

  anexarLinkModelo(modeloId: number, url: string, titulo?: string): Promise<ModeloTarefaDTO> {
    return firstValueFrom(
      this.httpClient.post<ModeloTarefaDTO>(`${environment.urlApi}/ModeloTarefa/${modeloId}/anexo-link`, { url, titulo })
    );
  }

  removerAnexoModelo(anexoId: number): Promise<ModeloTarefaDTO> {
    return firstValueFrom(
      this.httpClient.delete<ModeloTarefaDTO>(`${environment.urlApi}/ModeloTarefa/anexo/${anexoId}`)
    );
  }

  obterAnexoModelo(anexoId: number): Observable<Blob> {
    return this.httpClient.get(`${environment.urlApi}/ModeloTarefa/anexo/${anexoId}`, { responseType: 'blob' });
  }
}
